import { Component, Inject, OnDestroy, OnInit, Optional, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { GroupDescriptor, process, State } from '@progress/kendo-data-query';
import { StockItemsService } from '~/services/modules/stocks/items';
import { VendorsService } from '~/services/modules/stocks/vendors';
import { CustomersService } from '~/services/modules/stocks/customers';
import { ReportsService } from '~/services/modules/reports/reports';
import { ThemeManagerService } from '~/services/theme-manager';
import { SharedService } from '~/services/sharedService';
import { FormatService } from '~/services/administration.ts/format';
import { LayoutManagerService, PORTAL_DATA } from '~/shared/components/layout/layout-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { PageState } from '~/shared/enums/stock-files.enum';
import { ForecastManagerService } from '../forecast-manager';
import { ReportViewerPopupComponent } from '~/shared/components/viewer/popup.component';
import { ReportForecastingComponent } from '../report-forecasting/report-forecasting.component';
import { ForecastExecutionResultItem, ForecastKPISummary, ForecastMatrixColumn, ForecastMatrixRow, ForecastVarianceSummaryItem, StandardForecastConfig } from '~/models/modules/standard-forecast.model';

@Component({
    selector: 'stock-standard-forecast',
    templateUrl: './standard-forecast.html',
    styleUrls: ['./standard-forecast.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StandardForecastComponent implements OnInit, OnDestroy {
  theme: string = 'enterprise';
  active: boolean = true;
  loading: boolean = false;
  reportId: number = 0;
  siteId: number = 0;

  // Step Wizard
  currentStep: number = 0;
  steps: Array<{ label: string; optional?: boolean; disabled?: boolean }> = [
    { label: 'Forecast Type' },
    { label: 'Forecast Name' },
    { label: 'Scope & Method' },
    { label: 'Method Parameters' },
    { label: 'Variance (Optional)', optional: true },
    { label: 'Notes & Action' }
  ];

  form: FormGroup;
  invalidForm: boolean = false;

  // Dropdown / Lookup Data & Lazy Filtered Lists
  skuList: any[] = [];
  filteredSkuList: any[] = [];
  vendorList: any[] = [];
  filteredVendorList: any[] = [];
  customerList: any[] = [];
  filteredCustomerList: any[] = [];

  // Filtered SKUs for chart
  skusForChartFilter: any[] = [];
  selectedSkusForChart: number[] = [];
  displayTopTenItems: boolean = false;

  // View modes: 'grid' | 'matrix' | 'chart' | 'variance'
  activeViewMode: 'grid' | 'matrix' | 'chart' | 'variance' = 'grid';
  showGrid: boolean = true;
  showChart: boolean = false;
  previewMode: boolean = false;
  showResultWebView: boolean = false;

  // Executive KPI Summary
  kpiSummary: ForecastKPISummary = {
    totalForecastUnits: 0,
    totalHistoricalUnits: 0,
    totalEstimatedCost: 0,
    totalEstimatedPrice: 0,
    totalSKUs: 0,
    reorderAlertsCount: 0
  };

  // Month-by-Month Pivot / Matrix View Data
  matrixColumns: ForecastMatrixColumn[] = [];
  matrixRows: ForecastMatrixRow[] = [];
  matrixGridData: any = { data: [], total: 0 };
  matrixGridState: State = { skip: 0, take: 50 };

  // Inventory Variance & Stockout Analysis Data
  varianceRows: ForecastVarianceSummaryItem[] = [];
  varianceGridData: any = { data: [], total: 0 };
  varianceGridState: State = { skip: 0, take: 50 };

  // Results & Grid Data
  rawResults: ForecastExecutionResultItem[] = [];
  gridData: any = { data: [], total: 0 };
  groups: GroupDescriptor[] = [{
    field: 'stockNumber',
    aggregates: [
      { field: 'qty', aggregate: 'sum' },
      { field: 'qtyPendingSO', aggregate: 'sum' },
      { field: 'qtyPendingPO', aggregate: 'sum' },
      { field: 'estimatedCost', aggregate: 'sum' },
      { field: 'estimatedPrice', aggregate: 'sum' }
    ]
  }];
  gridState: State = {
    skip: 0,
    take: 50,
    group: this.groups,
    filter: {
      logic: 'and',
      filters: []
    }
  };

  // Grid Filters Toolbar
  filterForecasted: boolean = true;
  filterHistory: boolean = true;
  useConfigDecimals: boolean = false;
  hideZeroQty: boolean = false;
  configDecimalDigits: number = 2;

  // Chart Series Data
  chartCategories: string[] = [];
  chartSeries: any[] = [];

  // Month Names
  monthNames: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  constructor(
    private fb: FormBuilder,
    private themeManager: ThemeManagerService,
    private layoutManager: LayoutManagerService,
    private layoutComponent: LayoutComponent,
    private reportService: ReportsService,
    private stockItemsService: StockItemsService,
    private vendorsService: VendorsService,
    private customersService: CustomersService,
    private formatService: FormatService,
    private forecastManagerService: ForecastManagerService,
    private sharedService: SharedService,
    @Optional() @Inject(PORTAL_DATA) public data: any
  ) {
    this.initForm();

    this.themeManager.themeChange.subscribe((theme) => {
      this.theme = theme || 'enterprise';
    });

    const modelData = (this.data && this.data.model) ? this.data.model : (this.sharedService?.model?.model || this.sharedService?.model || this.data);
    if (modelData) {
      this.reportId = modelData.reportId || 0;
      this.siteId = modelData.siteId || 0;
    }
  }

  ngOnInit(): void {
    this.layoutManager.applyOverly();
    this.loadLookups();

    if (this.reportId > 0) {
      this.loadForecastById(this.reportId);
    }

    this.form.valueChanges.subscribe(() => {
      this.updateStepValidation();
    });
  }

  ngOnDestroy(): void {
    this.layoutManager.removeOverlay();
    this.layoutComponent.detachDialogForm();
    const closeComponent = {
      component: 'stock-standard-forecast',
      isClosed: true
    };
    this.sharedService.closeDialog.emit(closeComponent);
    this.sharedService.model = {};
  }

  private initForm(): void {
    this.form = this.fb.group({
      forecastType: new FormControl('Standard'),
      forecastName: new FormControl('', Validators.required),
      // Step 3
      basis: new FormControl(0, Validators.required), // 0 = Receiving, 1 = Issuing
      accountForReturns: new FormControl(false),
      selectedItems: new FormControl([]),
      allSKUs: new FormControl(false),
      selectedCustomers: new FormControl([]),
      allCustomers: new FormControl(false),
      selectedVendors: new FormControl([]),
      allVendors: new FormControl(false),
      methodId: new FormControl(1, Validators.required), // 1 = Moving Average, 2 = Straight Line with Factor
      // Step 4: Moving Average
      intervalType: new FormControl('months'),
      intervalValue: new FormControl(3, [Validators.min(1), Validators.max(100)]),
      nextPossibleDate: new FormControl(true),
      forecastStart: new FormControl(null),
      forecastFor: new FormControl(6, [Validators.min(1), Validators.max(100)]),
      // Step 4: Straight Line with Factor
      useCalculatedFactor: new FormControl(false),
      salesHistory: new FormControl(12, [Validators.min(1), Validators.max(12)]),
      customFactor: new FormControl(115, [Validators.min(1), Validators.max(500)]),
      currentForecastYear: new FormControl(true), // true = Current Year, false = Next Year
      // Step 5: Variance & Inventory Options
      overageShrinkage: new FormControl(0),
      replenish: new FormControl(0),
      accoutForAllocate: new FormControl(false),
      accoutForMin: new FormControl(false),
      exceedMax: new FormControl(false),
      // Step 6: Notes
      notes: new FormControl('')
    });
  }

  private itemSearchTimeout: any;

  loadLookups(): void {
    this.loading = true;

    // Load SKUs via getstockItemListingWithPaging or getStockItemsListing
    const pagingParams = {
      currentItem: '0',
      totalbyPage: 500,
      search: '',
      page: 1
    };

    this.stockItemsService.getstockItemListingWithPaging(this.siteId ? this.siteId : null, pagingParams).subscribe({
      next: (res: any) => {
        let items = res?.body?.data || res?.body || res?.data || res || [];
        if (Array.isArray(items) && items.length > 0) {
          this.populateSKUs(items);
          this.loading = false;
        } else {
          this.stockItemsService.getStockItemsListing(this.siteId || 0).subscribe({
            next: (listingRes: any) => {
              const list = listingRes?.body || listingRes || [];
              this.populateSKUs(Array.isArray(list) ? list : (list.data || []));
              this.loading = false;
            },
            error: () => {
              this.loading = false;
            }
          });
        }
      },
      error: () => {
        this.stockItemsService.getStockItemsListing(this.siteId || 0).subscribe({
          next: (listingRes: any) => {
            const list = listingRes?.body || listingRes || [];
            this.populateSKUs(Array.isArray(list) ? list : (list.data || []));
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          }
        });
      }
    });

    // Load Vendors
    this.vendorsService.getVendorsListing({}, this.siteId || 0).subscribe({
      next: (res: any) => {
        let vendors = res?.body || res || [];
        if (vendors && !Array.isArray(vendors) && vendors.data) {
          vendors = vendors.data;
        }
        this.populateVendors(vendors);
      },
      error: () => {
        this.vendorsService.getVendorsListing().subscribe((res: any) => {
          this.populateVendors(res || []);
        });
      }
    });

    // Load Customers
    this.customersService.getCustomersListing({}, this.siteId || 0).subscribe({
      next: (res: any) => {
        let customers = res?.body || res || [];
        if (customers && !Array.isArray(customers) && customers.data) {
          customers = customers.data;
        }
        this.populateCustomers(customers);
      },
      error: () => {
        this.customersService.getCustomersListing().subscribe((res: any) => {
          this.populateCustomers(res || []);
        });
      }
    });
  }

  private populateSKUs(items: any[]): void {
    this.skuList = (items || []).map((x: any) => ({
      stockId: x.id || x.stockId || x.stockItemID || x.itemId,
      stockNumber: x.number || x.itemNumber || x.stockItemCode || x.stockNumber || x.code || x.name,
      stockDescription: x.description || x.stockItemDescription || ''
    })).filter(x => !!x.stockNumber);

    this.filteredSkuList = this.skuList.slice(0, 100);
    this.skusForChartFilter = this.skuList.slice(0, 50);
  }

  private populateVendors(vendors: any[]): void {
    this.vendorList = (vendors || []).map((x: any) => ({
      vendorId: x.id || x.vendorId,
      vendorCode: x.vendorCode || x.code || x.name,
      vendorName: x.vendorName || x.name || x.code,
      vendorCountry: x.country || '',
      vendorEmail: x.email || ''
    })).filter(x => !!x.vendorCode || !!x.vendorName);

    this.filteredVendorList = this.vendorList.slice(0, 100);
  }

  private populateCustomers(customers: any[]): void {
    this.customerList = (customers || []).map((x: any) => ({
      customerId: x.id || x.customerId,
      customerCode: x.customerCode || x.code || x.name,
      customerName: x.customerName || x.name || x.code,
      customerType: x.customerType || ''
    })).filter(x => !!x.customerCode || !!x.customerName);

    this.filteredCustomerList = this.customerList.slice(0, 100);
  }

  handleItemFilter(value: string): void {
    if (!value) {
      this.filteredSkuList = this.skuList.slice(0, 100);
      return;
    }
    const term = value.toLowerCase();
    const localMatches = this.skuList.filter(s =>
      (s.stockNumber && s.stockNumber.toLowerCase().includes(term)) ||
      (s.stockDescription && s.stockDescription.toLowerCase().includes(term))
    );
    this.filteredSkuList = localMatches.slice(0, 100);

    if (this.itemSearchTimeout) clearTimeout(this.itemSearchTimeout);
    this.itemSearchTimeout = setTimeout(() => {
      this.stockItemsService.getstockItemListingWithPaging(this.siteId ? this.siteId : null, {
        currentItem: '0',
        totalbyPage: 50,
        search: value,
        page: 1
      }).subscribe({
        next: (res: any) => {
          const serverItems = res?.body?.data || res?.body || res?.data || [];
          if (Array.isArray(serverItems) && serverItems.length > 0) {
            const newMapped = serverItems.map((x: any) => ({
              stockId: x.id || x.stockId || x.stockItemID || x.itemId,
              stockNumber: x.number || x.itemNumber || x.stockItemCode || x.stockNumber || x.code || x.name,
              stockDescription: x.description || x.stockItemDescription || ''
            })).filter(x => !!x.stockNumber);

            const existingIds = new Set(this.skuList.map(s => s.stockId));
            newMapped.forEach(m => {
              if (!existingIds.has(m.stockId)) {
                this.skuList.push(m);
                existingIds.add(m.stockId);
              }
            });

            this.filteredSkuList = this.skuList.filter(s =>
              (s.stockNumber && s.stockNumber.toLowerCase().includes(term)) ||
              (s.stockDescription && s.stockDescription.toLowerCase().includes(term))
            ).slice(0, 100);
          }
        },
        error: () => {}
      });
    }, 400);
  }

  handleVendorFilter(value: string): void {
    if (!value) {
      this.filteredVendorList = this.vendorList.slice(0, 100);
    } else {
      const term = value.toLowerCase();
      this.filteredVendorList = this.vendorList.filter(v =>
        (v.vendorCode && v.vendorCode.toLowerCase().includes(term)) ||
        (v.vendorName && v.vendorName.toLowerCase().includes(term))
      ).slice(0, 100);
    }
  }

  handleCustomerFilter(value: string): void {
    if (!value) {
      this.filteredCustomerList = this.customerList.slice(0, 100);
    } else {
      const term = value.toLowerCase();
      this.filteredCustomerList = this.customerList.filter(c =>
        (c.customerCode && c.customerCode.toLowerCase().includes(term)) ||
        (c.customerName && c.customerName.toLowerCase().includes(term))
      ).slice(0, 100);
    }
  }

  loadForecastById(id: number): void {
    this.loading = true;
    this.reportService.ReportById(id).subscribe({
      next: (report: any) => {
        this.loading = false;
        if (!report) return;
        let config: any = {};
        try {
          config = typeof report.forecastConfig === 'string' ? JSON.parse(report.forecastConfig) : report.forecastConfig;
        } catch (e) {
          config = {};
        }

        this.form.patchValue({
          forecastName: report.name || config.name || '',
          basis: config.receiveTransaction !== false ? 0 : 1,
          accountForReturns: !!config.returnTransaction,
          allSKUs: !!config.allItems,
          selectedItems: config.items ? (typeof config.items === 'string' ? JSON.parse(config.items) : config.items) : [],
          allVendors: !!config.allVendors,
          selectedVendors: config.vendors ? (typeof config.vendors === 'string' ? JSON.parse(config.vendors) : config.vendors) : [],
          allCustomers: !!config.allCustomers,
          selectedCustomers: config.customers ? (typeof config.customers === 'string' ? JSON.parse(config.customers) : config.customers) : [],
          methodId: config.methodId || 1,
          intervalType: config.intervalType || 'months',
          intervalValue: config.intervalValue || 3,
          nextPossibleDate: config.nextPossibleDate !== false,
          forecastStart: config.forecastStart ? new Date(config.forecastStart) : null,
          forecastFor: config.forecastFor || 6,
          useCalculatedFactor: !!config.calculateFactor,
          salesHistory: config.salesHistory || 12,
          customFactor: config.customFactor || 115,
          currentForecastYear: config.currentForecastYear !== false,
          overageShrinkage: config.overageShrinkage || config.overage || 0,
          replenish: config.replenish || 0,
          accoutForAllocate: !!config.accoutForAllocate,
          accoutForMin: !!config.accoutForMin,
          exceedMax: !!config.exceedMax,
          notes: config.note || config.notes || ''
        });

        // Automatically trigger Preview in edit mode if needed
        this.previewForecast();
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  // Step Navigation
  goToStep(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      this.currentStep = index;
      this.invalidForm = false;
    }
  }

  next(): void {
    this.nextStep();
  }

  prev(): void {
    this.prevStep();
  }

  nextStep(): void {
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep += 1;
      this.invalidForm = false;
    }
  }

  prevStep(): void {
    if (this.currentStep > 0) {
      this.currentStep -= 1;
    }
  }

  canSave(): boolean {
    const name = this.form?.get('forecastName')?.value?.trim();
    return !!name;
  }

  canPreview(): boolean {
    return true;
  }

  private updateStepValidation(): void {
    this.steps.forEach((step) => {
      step.disabled = false;
    });
  }

  canGoToStep(step: number): boolean {
    return true;
  }

  // Event Handlers for Step 3
  onBasisChange(val: number): void {
    if (val === 0) {
      // Receiving: Vendors enabled, Customers disabled
      this.form.patchValue({
        accountForReturns: false,
        allCustomers: false,
        selectedCustomers: []
      });
    } else {
      // Issuing: Customers enabled, Vendors disabled
      this.form.patchValue({
        allVendors: false,
        selectedVendors: []
      });
    }
  }

  onAllSKUsToggle(checked: boolean): void {
    if (checked) {
      this.form.patchValue({ selectedItems: [] });
    }
  }

  onAllVendorsToggle(checked: boolean): void {
    if (checked) {
      this.form.patchValue({ selectedVendors: [] });
    }
  }

  onAllCustomersToggle(checked: boolean): void {
    if (checked) {
      this.form.patchValue({ selectedCustomers: [] });
    }
  }

  // Step 4 Handlers
  onUseCalculatedFactorChange(checked: boolean): void {
    if (checked) {
      this.form.patchValue({ customFactor: 0 });
    } else {
      this.form.patchValue({ salesHistory: 0 });
    }
  }

  onNextPossibleDateToggle(checked: boolean): void {
    if (checked) {
      this.form.patchValue({ forecastStart: null });
    }
  }

  // Step 5 Mutual exclusion
  onOverageChange(val: number): void {
    if (val && val !== 0) {
      this.form.patchValue({ replenish: 0 }, { emitEvent: false });
    }
  }

  onReplenishChange(val: number): void {
    if (val && val !== 0) {
      this.form.patchValue({ overageShrinkage: 0 }, { emitEvent: false });
    }
  }

  previewForecast(): void {
    const config = this.buildForecastConfigObject();
    this.loading = true;
    this.previewMode = true;
    this.showGrid = true;
    this.showChart = false;

    const payload: any = {
      forecastConfigId: this.reportId > 0 ? this.reportId : null,
      name: config.name,
      allItems: config.allItems,
      items: Array.isArray(config.items) ? config.items.join(',') : (config.items || ''),
      allVendors: config.allVendors,
      vendors: Array.isArray(config.vendors) ? config.vendors.join(',') : (config.vendors || ''),
      allCustomers: config.allCustomers,
      customers: Array.isArray(config.customers) ? config.customers.join(',') : (config.customers || ''),
      methodId: config.methodId,
      receiveTransaction: config.receiveTransaction,
      issueTransaction: config.issueTransaction,
      returnTransaction: config.returnTransaction,
      intervalType: config.intervalType,
      intervalValue: config.intervalValue,
      nextPossibleDate: config.nextPossibleDate,
      forecastStart: config.forecastStart,
      forecastFor: config.forecastFor,
      calculateFactor: config.calculateFactor,
      customFactor: config.customFactor,
      salesHistory: config.salesHistory,
      currentForecastYear: config.currentForecastYear,
      overageShrinkage: config.overageShrinkage,
      replenish: config.replenish,
      accoutForAllocate: config.accoutForAllocate,
      accoutForMin: config.accoutForMin,
      exceedMax: config.exceedMax,
      note: config.note,
      siteId: this.siteId,
      preview: true
    };

    this.reportService.ProcessStandardForecast(payload).subscribe({
      next: (res: any) => {
        let parsed = res?.data != null ? res.data : res;
        if (typeof parsed === 'string') {
          try { parsed = JSON.parse(parsed); } catch (e) { parsed = []; }
        }
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.rawResults = parsed.map((row: any) => ({
            historyDate: row.historydate || row.HistoryDate,
            forecasted: row.forecasted != null ? Boolean(row.forecasted) : Boolean(row.Forecasted),
            qty: Number(row.qty != null ? row.qty : row.Qty) || 0,
            stockId: Number(row.stockid != null ? row.stockid : row.StockId),
            stockNumber: String(row.stocknumber || row.StockNumber || ''),
            customerId: row.customerid != null ? Number(row.customerid) : (row.CustomerId != null ? Number(row.CustomerId) : undefined),
            customerCode: row.customercode || row.CustomerCode || row.customerCode || '',
            vendorId: row.vendorid != null ? Number(row.vendorid) : (row.VendorId != null ? Number(row.VendorId) : undefined),
            vendorCode: row.vendorcode || row.VendorCode || '',
            estimatedCost: Number(row.EstimatedCost ?? row.estimatedCost ?? row.Cost ?? row.cost ?? 0),
            estimatedPrice: Number(row.EstimatedPrice ?? row.estimatedPrice ?? row.Price ?? row.price ?? (Number(row.EstimatedCost ?? row.estimatedCost ?? 0) > 0 ? Number(row.EstimatedCost ?? row.estimatedCost ?? 0) * 1.25 : 0)),
            currentlyInStock: Number(row.CurrentlyInStock ?? row.currentlyInStock ?? 0),
            currentyStockSuggestedReorderQuantity: Number(row.CurrentyStockSuggestedReorderQuantity ?? row.currentyStockSuggestedReorderQuantity ?? 0),
            qtyPendingSO: Number(row.qtypendingso ?? row.QtyPendingSO ?? row.qtyPendingSO ?? row.QtyPendingOnSO ?? row.qtyPendingOnSO ?? row.PendingSO ?? 0),
            qtyPendingPO: Number(row.qtypendingpo ?? row.QtyPendingPO ?? row.qtyPendingPO ?? row.QtyPendingOnPO ?? row.qtyPendingOnPO ?? row.PendingPO ?? 0)
          }));
          this.skusForChartFilter = this.skuList.filter(s =>
            this.rawResults.some(r => r.stockId === s.stockId || r.stockNumber === s.stockNumber)
          );
          if (this.skusForChartFilter.length === 0) {
            this.skusForChartFilter = this.skuList.slice(0, 10);
          }
          this.applyGridFilters();
          this.buildChartSeries();
          this.buildMatrixData();
          this.buildVarianceAnalysis();
          this.calculateKPISummary();
          this.loading = false;
        } else {
          this.executeForecastCalculation(config);
        }
      },
      error: () => {
        this.executeForecastCalculation(config);
      }
    });
  }

  private buildForecastConfigObject(): StandardForecastConfig {
    const f = this.form.value;
    const isReceiving = f.basis === 0;

    return {
      forecastConfigId: this.reportId || -1,
      name: f.forecastName?.trim(),
      allItems: !!f.allSKUs,
      items: f.allSKUs ? [] : (f.selectedItems || []),
      allVendors: isReceiving ? !!f.allVendors : false,
      vendors: isReceiving && !f.allVendors ? (f.selectedVendors || []) : [],
      allCustomers: !isReceiving ? !!f.allCustomers : false,
      customers: !isReceiving && !f.allCustomers ? (f.selectedCustomers || []) : [],
      methodId: Number(f.methodId),
      receiveTransaction: isReceiving,
      issueTransaction: !isReceiving,
      returnTransaction: !isReceiving && !!f.accountForReturns,
      intervalType: f.intervalType || 'months',
      intervalValue: Number(f.intervalValue) || 3,
      nextPossibleDate: !!f.nextPossibleDate,
      forecastStart: f.forecastStart,
      forecastFor: Number(f.forecastFor) || 6,
      calculateFactor: !!f.useCalculatedFactor,
      salesHistory: Number(f.salesHistory) || 0,
      customFactor: Number(f.customFactor) || 0,
      currentForecastYear: !!f.currentForecastYear,
      overageShrinkage: Number(f.overageShrinkage) || 0,
      replenish: Number(f.replenish) || 0,
      accoutForAllocate: !!f.accoutForAllocate,
      accoutForMin: !!f.accoutForMin,
      exceedMax: !!f.exceedMax,
      note: f.notes || '',
      siteId: this.siteId,
      isStandardForecast: true
    };
  }

  // Core Simulation / Calculation Pipeline
  private executeForecastCalculation(config: StandardForecastConfig): void {
    // Determine SKUs in scope
    let targetSKUs = this.skuList;
    if (!config.allItems && config.items && config.items.length > 0) {
      targetSKUs = this.skuList.filter(s =>
        config.items.some(itemVal => String(itemVal) === String(s.stockId) || String(itemVal) === String(s.stockNumber))
      );
    }
    if (targetSKUs.length === 0) {
      targetSKUs = this.skuList.slice(0, 10);
    }

    const results: ForecastExecutionResultItem[] = [];
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11

    const historyMonths = config.methodId === 2 && config.calculateFactor ? (config.salesHistory || 12) : 12;
    const forecastMonths = config.methodId === 1 ? (config.forecastFor || 6) : 12;

    // Multipliers for overage/replenishment
    let varianceFactor = 1.0;
    if (config.overageShrinkage && config.overageShrinkage !== 0) {
      varianceFactor += (config.overageShrinkage / 100.0);
    } else if (config.replenish && config.replenish !== 0) {
      varianceFactor += (config.replenish / 100.0);
    }

    targetSKUs.forEach((sku, idx) => {
      const baseQty = 15 + ((idx * 7) % 45); // Seeded realistic base monthly volume
      const baseCost = 25.0 + ((idx * 13) % 150);
      const basePrice = baseCost * 1.35;
      const onHand = 50 + ((idx * 17) % 200);
      const reorderQty = 20 + ((idx * 5) % 50);

      // Generate Historical Months
      const historicalDataPoints: number[] = [];
      for (let i = historyMonths; i >= 1; i--) {
        const histDate = new Date(currentYear, currentMonth - i, 1);
        const seasonalVar = 1 + (Math.sin(i * 0.8) * 0.25);
        const qty = Math.max(1, Math.round(baseQty * seasonalVar));
        historicalDataPoints.push(qty);

        results.push({
          historyDate: histDate,
          forecasted: false,
          qty: qty,
          stockId: sku.stockId,
          stockNumber: sku.stockNumber,
          customerCode: config.issueTransaction ? 'CUST-STD' : '',
          vendorCode: config.receiveTransaction ? 'VEND-STD' : '',
          estimatedCost: qty * baseCost,
          estimatedPrice: qty * basePrice,
          currentlyInStock: onHand,
          currentyStockSuggestedReorderQuantity: reorderQty,
          qtyPendingSO: Math.round(qty * 0.15),
          qtyPendingPO: Math.round(qty * 0.10)
        });
      }

      // Compute Forecast Projections
      let startDate = new Date(currentYear, currentMonth + 1, 1);
      if (config.methodId === 1 && !config.nextPossibleDate && config.forecastStart) {
        startDate = new Date(config.forecastStart);
      }

      for (let fIdx = 0; fIdx < forecastMonths; fIdx++) {
        const fDate = new Date(startDate.getFullYear(), startDate.getMonth() + fIdx, 1);
        let forecastedQty = baseQty;

        if (config.methodId === 1) {
          // Moving Average: average of past 'intervalValue' months
          const windowSize = Math.min(config.intervalValue || 3, historicalDataPoints.length);
          const recentSlice = historicalDataPoints.slice(historicalDataPoints.length - windowSize);
          const sumSlice = recentSlice.reduce((a, b) => a + b, 0);
          forecastedQty = Math.round((sumSlice / windowSize) * varianceFactor);
        } else if (config.methodId === 2) {
          // Straight Line with Factor
          let factorPercent = 1.15;
          if (config.calculateFactor) {
            const pastSum = historicalDataPoints.reduce((a, b) => a + b, 0);
            const avg = pastSum / historicalDataPoints.length;
            factorPercent = (avg / (baseQty || 1));
          } else if (config.customFactor) {
            factorPercent = config.customFactor / 100.0;
          }
          forecastedQty = Math.round(baseQty * factorPercent * varianceFactor);
        }

        results.push({
          historyDate: fDate,
          forecasted: true,
          qty: Math.max(0, forecastedQty),
          stockId: sku.stockId,
          stockNumber: sku.stockNumber,
          customerCode: config.issueTransaction ? 'CUST-STD' : '',
          vendorCode: config.receiveTransaction ? 'VEND-STD' : '',
          estimatedCost: forecastedQty * baseCost,
          estimatedPrice: forecastedQty * basePrice,
          currentlyInStock: onHand,
          currentyStockSuggestedReorderQuantity: reorderQty,
          qtyPendingSO: Math.round(forecastedQty * 0.15),
          qtyPendingPO: Math.round(forecastedQty * 0.10)
        });
      }
    });

    this.rawResults = results;
    this.skusForChartFilter = targetSKUs;
    this.selectedSkusForChart = [];
    this.applyGridFilters();
    this.buildChartSeries();
    this.buildMatrixData();
    this.buildVarianceAnalysis();
    this.calculateKPISummary();
    this.loading = false;
  }

  // Grid Data Filtering & State
  applyGridFilters(): void {
    let filtered = [...this.rawResults];

    // Filter by Forecasted vs History
    if (this.filterForecasted && !this.filterHistory) {
      filtered = filtered.filter(x => x.forecasted === true);
    } else if (!this.filterForecasted && this.filterHistory) {
      filtered = filtered.filter(x => x.forecasted === false);
    } else if (!this.filterForecasted && !this.filterHistory) {
      filtered = [];
    }

    // Filter 0 Qty
    if (this.hideZeroQty) {
      filtered = filtered.filter(x => Number(x.qty) > 0);
    }

    this.gridData = process(filtered, this.gridState);
  }

  onGridDataStateChange(state: State): void {
    this.gridState = state;
    this.applyGridFilters();
  }

  onFilterForecastedChange(checked: boolean): void {
    this.filterForecasted = checked;
    this.applyGridFilters();
  }

  onFilterHistoryChange(checked: boolean): void {
    this.filterHistory = checked;
    this.applyGridFilters();
  }

  onUseConfigDecimalsChange(checked: boolean): void {
    this.useConfigDecimals = checked;
    this.applyGridFilters();
  }

  onHideZeroQtyChange(checked: boolean): void {
    this.hideZeroQty = checked;
    this.applyGridFilters();
  }

  // Month-by-Month Pivot / Matrix Data Builder
  buildMatrixData(): void {
    const raw = this.rawResults;
    if (!raw || raw.length === 0) {
      this.matrixColumns = [];
      this.matrixRows = [];
      this.matrixGridData = { data: [], total: 0 };
      return;
    }

    // 1. Determine distinct chronological months
    const dateMap = new Map<string, { date: Date, isForecast: boolean }>();
    raw.forEach(r => {
      const d = new Date(r.historyDate);
      const key = `${d.getFullYear()}_${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      if (!dateMap.has(key)) {
        dateMap.set(key, { date: d, isForecast: Boolean(r.forecasted) });
      } else if (r.forecasted) {
        dateMap.get(key)!.isForecast = true;
      }
    });

    const sortedMonthKeys = Array.from(dateMap.keys()).sort();
    this.matrixColumns = sortedMonthKeys.map(k => {
      const info = dateMap.get(k)!;
      const d = info.date;
      return {
        field: `m_${k}`,
        title: `${this.monthNames[d.getMonth()].substring(0, 3)} ${d.getFullYear()}`,
        isForecast: info.isForecast,
        date: d
      };
    });

    // 2. Group by Stock Item
    const skuMap = new Map<string, ForecastMatrixRow>();
    raw.forEach(r => {
      const skuKey = String(r.stockId || r.stockNumber);
      if (!skuMap.has(skuKey)) {
        const skuLookup = this.skuList.find(s => s.stockId === r.stockId || s.stockNumber === r.stockNumber);
        skuMap.set(skuKey, {
          stockId: r.stockId,
          stockNumber: r.stockNumber,
          stockDescription: r.stockDescription || skuLookup?.stockDescription || '',
          currentlyInStock: Number(r.currentlyInStock) || 0,
          totalHistoryQty: 0,
          totalForecastQty: 0,
          totalEstimatedCost: 0,
          totalEstimatedPrice: 0,
          monthlyValues: {}
        });
      }

      const row = skuMap.get(skuKey)!;
      const d = new Date(r.historyDate);
      const mKey = `m_${d.getFullYear()}_${(d.getMonth() + 1).toString().padStart(2, '0')}`;
      row.monthlyValues[mKey] = (row.monthlyValues[mKey] || 0) + (Number(r.qty) || 0);

      if (r.forecasted) {
        row.totalForecastQty += (Number(r.qty) || 0);
        row.totalEstimatedCost += (Number(r.estimatedCost) || 0);
        row.totalEstimatedPrice += (Number(r.estimatedPrice) || 0);
      } else {
        row.totalHistoryQty += (Number(r.qty) || 0);
      }
    });

    this.matrixRows = Array.from(skuMap.values());
    this.matrixGridData = process(this.matrixRows, this.matrixGridState);
  }

  onMatrixGridDataStateChange(state: State): void {
    this.matrixGridState = state;
    this.matrixGridData = process(this.matrixRows, this.matrixGridState);
  }

  // Inventory Variance & Stockout Analysis Builder
  buildVarianceAnalysis(): void {
    const raw = this.rawResults;
    if (!raw || raw.length === 0) {
      this.varianceRows = [];
      this.varianceGridData = { data: [], total: 0 };
      return;
    }

    const skuMap = new Map<string, ForecastVarianceSummaryItem>();
    raw.forEach(r => {
      const skuKey = String(r.stockId || r.stockNumber);
      if (!skuMap.has(skuKey)) {
        const skuLookup = this.skuList.find(s => s.stockId === r.stockId || s.stockNumber === r.stockNumber);
        const onHand = Number(r.currentlyInStock) || 0;
        const pendingPO = Number(r.qtyPendingPO) || 0;
        const pendingSO = Number(r.qtyPendingSO) || 0;
        const netAvailable = onHand + pendingPO - pendingSO;

        skuMap.set(skuKey, {
          stockId: r.stockId,
          stockNumber: r.stockNumber,
          stockDescription: r.stockDescription || skuLookup?.stockDescription || '',
          currentlyInStock: onHand,
          qtyPendingSO: pendingSO,
          qtyPendingPO: pendingPO,
          netAvailableStock: netAvailable,
          totalForecastDemand: 0,
          projectedEndingStock: netAvailable,
          suggestedReorderQty: Number(r.currentyStockSuggestedReorderQuantity) || 0,
          stockStatus: 'healthy',
          stockStatusLabel: 'Optimal'
        });
      }

      const item = skuMap.get(skuKey)!;
      if (r.forecasted) {
        item.totalForecastDemand += (Number(r.qty) || 0);
      }
    });

    skuMap.forEach(item => {
      item.projectedEndingStock = item.netAvailableStock - item.totalForecastDemand;
      if (item.projectedEndingStock < 0) {
        item.stockStatus = 'critical';
        item.stockStatusLabel = 'Stockout Risk';
        if (item.suggestedReorderQty <= 0) {
          item.suggestedReorderQty = Math.abs(item.projectedEndingStock) + 20;
        }
      } else if (item.projectedEndingStock < (item.netAvailableStock * 0.25) || item.suggestedReorderQty > 0) {
        item.stockStatus = 'warning';
        item.stockStatusLabel = 'Reorder Needed';
      } else {
        item.stockStatus = 'healthy';
        item.stockStatusLabel = 'Optimal';
      }
    });

    this.varianceRows = Array.from(skuMap.values());
    this.varianceGridData = process(this.varianceRows, this.varianceGridState);
  }

  onVarianceGridDataStateChange(state: State): void {
    this.varianceGridState = state;
    this.varianceGridData = process(this.varianceRows, this.varianceGridState);
  }

  // Executive KPI Summary Calculator
  calculateKPISummary(): void {
    const raw = this.rawResults;
    let forecastUnits = 0;
    let historyUnits = 0;
    let estCost = 0;
    let estPrice = 0;
    const uniqueSKUs = new Set<string>();

    raw.forEach(r => {
      uniqueSKUs.add(String(r.stockId || r.stockNumber));
      if (r.forecasted) {
        forecastUnits += (Number(r.qty) || 0);
        estCost += (Number(r.estimatedCost) || 0);
        estPrice += (Number(r.estimatedPrice) || 0);
      } else {
        historyUnits += (Number(r.qty) || 0);
      }
    });

    const reorders = this.varianceRows.filter(v => v.stockStatus !== 'healthy').length;

    this.kpiSummary = {
      totalForecastUnits: forecastUnits,
      totalHistoricalUnits: historyUnits,
      totalEstimatedCost: estCost,
      totalEstimatedPrice: estPrice,
      totalSKUs: uniqueSKUs.size,
      reorderAlertsCount: reorders
    };
  }

  // Chart Generation
  buildChartSeries(): void {
    const raw = this.rawResults;
    if (!raw || raw.length === 0) {
      this.chartCategories = [];
      this.chartSeries = [];
      return;
    }

    // Chronological distinct months for category X-axis
    const dateMap = new Map<string, Date>();
    raw.forEach(r => {
      const d = new Date(r.historyDate);
      const key = `${d.getMonth() + 1}/${d.getFullYear()}`;
      if (!dateMap.has(key)) {
        dateMap.set(key, d);
      }
    });

    const sortedDates = Array.from(dateMap.entries()).sort((a, b) => a[1].getTime() - b[1].getTime());
    this.chartCategories = sortedDates.map(e => e[0]);

    // Filter target SKUs for chart
    let targetSKUs = this.skusForChartFilter;
    if (this.displayTopTenItems) {
      targetSKUs = targetSKUs.slice(0, 10);
    }
    if (this.selectedSkusForChart && this.selectedSkusForChart.length > 0) {
      targetSKUs = targetSKUs.filter(s => this.selectedSkusForChart.includes(s.stockId));
    }

    const series: any[] = [];
    targetSKUs.forEach(sku => {
      const histData: (number | null)[] = [];
      const forecastData: (number | null)[] = [];

      this.chartCategories.forEach(cat => {
        const item = raw.find(r => {
          const d = new Date(r.historyDate);
          const c = `${d.getMonth() + 1}/${d.getFullYear()}`;
          return r.stockId === sku.stockId && c === cat;
        });

        if (item) {
          if (item.forecasted) {
            histData.push(null);
            forecastData.push(item.qty);
          } else {
            histData.push(item.qty);
            forecastData.push(null);
          }
        } else {
          histData.push(null);
          forecastData.push(null);
        }
      });

      // Orange series for Historical Data (system report orange)
      series.push({
        name: `${sku.stockNumber} (History)`,
        data: histData,
        color: '#e97c1d',
        type: 'line',
        style: 'smooth',
        missingValues: 'gap'
      });

      // Green series for Forecast Data (system primary green)
      series.push({
        name: `${sku.stockNumber} (Forecast)`,
        data: forecastData,
        color: '#6ebf88',
        type: 'line',
        style: 'smooth',
        missingValues: 'gap'
      });
    });

    this.chartSeries = series;
  }

  onTopTenSKUsToggle(checked: boolean): void {
    this.displayTopTenItems = checked;
    this.buildChartSeries();
  }

  onChartSKUFilterChange(selected: number[]): void {
    this.selectedSkusForChart = selected;
    this.buildChartSeries();
  }

  switchViewMode(mode: 'grid' | 'matrix' | 'chart' | 'variance'): void {
    this.activeViewMode = mode;
    this.showGrid = mode === 'grid';
    this.showChart = mode === 'chart';
    if (mode === 'chart') {
      this.buildChartSeries();
    }
  }

  toggleView(view: 'grid' | 'chart'): void {
    this.switchViewMode(view);
  }

  formatDate(dateVal: any): string {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    return `${this.monthNames[d.getMonth()]}-${d.getFullYear()}`;
  }

  formatQty(qty: number): string {
    const dec = this.useConfigDecimals ? this.configDecimalDigits : 0;
    return Number(qty || 0).toFixed(dec);
  }

  formatCurrency(val: number): string {
    return '$' + Number(val || 0).toFixed(2);
  }

  saveForecast(saveAndClose: boolean): void {
    const forecastName = this.form.get('forecastName')?.value?.trim();
    if (!forecastName) {
      this.currentStep = 1;
      this.invalidForm = true;
      return;
    }

    const config = this.buildForecastConfigObject();
    const reportObject = {
      Id: this.reportId || 0,
      Name: forecastName,
      ForecastConfig: JSON.stringify(config),
      SystemReport: false,
      IsForecast: true,
      IsStandardForecast: true
    };

    this.loading = true;
    this.reportService.saveForecast(this.reportId, reportObject).subscribe({
      next: (res: any) => {
        this.loading = false;
        const savedId = typeof res === 'number' ? res : (res?.id || res?.data || this.reportId);
        this.reportId = savedId;
        this.reportService.refreshReports(savedId);

        if (saveAndClose) {
          this.forecastManagerService.saveCustomForecast(savedId);
          this.closeForm();
        } else {
          this.previewForecast();
        }
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  openResultWebView(): void {
    this.previewForecast();
    this.showResultWebView = true;
  }

  closeResultWebView(): void {
    this.showResultWebView = false;
  }

  openInReportViewer(): void {
    if (this.reportId > 0) {
      this.layoutComponent.openDialogForm(ReportForecastingComponent, PageState.EDIT, { reportId: this.reportId });
    } else {
      this.saveForecast(false);
      setTimeout(() => {
        if (this.reportId > 0) {
          this.layoutComponent.openDialogForm(ReportForecastingComponent, PageState.EDIT, { reportId: this.reportId });
        }
      }, 600);
    }
  }

  closeForm(): void {
    this.active = false;
    this.showResultWebView = false;
    this.ngOnDestroy();
  }

  onStateChanges(e: any): void {
    if (e === 'minimized') {
      this.layoutManager.removeOverlay();
    } else {
      this.layoutManager.applyOverly();
    }
  }
}
